import { Stack, StackItem } from '@fluentui/react';
import { ProgressBar, Skeleton, SkeletonItem, Spinner, Text } from '@fluentui/react-components';
import { Database32Regular, DataUsageRegular } from '@fluentui/react-icons';
import { DatahubFilterType, DatahubSelectorDialogResult, NotificationToastDuration, NotificationType, useFabricContext } from '@idmc-ms-fabric/integration';
import { CoreAttr } from '@infa-hawk/models';
import React, { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import AssetCard, { AssetCardSkeleton } from '../../component/core/asset-card/AssetCard';
import ErrorScreen from '../../component/core/error/ErrorScreen';
import Page from '../../component/shared/Page';
import { useIDQALoggerContext } from '../../context/business/IDQAlogger/IDQALoggerContext';
import { useLoginContext } from '../../context/business/InfaLogin/InfaLoginContext';
import useDQTranslation from '../../hooks/useDQTranslation';
import i18nKeys from '../../i18n/i18nKeys';
import { SupportedCatalogSourceType } from '../../utils/catalog-source/catalog-source';
import catalogSourceBuilder from '../../utils/catalog-source/catalog-source-builder';
import { SupportedArtifactType } from '../../utils/common-types';
import constants from '../../utils/constants';
import { generateArtifactName } from '../../utils/coreUtils';
import { getFabricDetails, searchCatalogFromExternalURIs } from '../../utils/infa-utils';
import { getItemType, getWorkloadName } from '../../utils/manifest-utils';
import { buildURL } from '../../utils/navigation-utils';
import { callArtifactCreate, parseFabricException } from '../../utils/trident-utils';
import ROUTES from '../Routes';
import useStyles from './DataSourceStyles';
type SelectedArtifactDatahub = Required<DatahubSelectorDialogResult>['selectedDatahubItem'][number];

export const DataSourceSkeleton: FC = () => (
	<Stack className={'stackPanel'}>
		<Stack.Item>
			<Skeleton style={{ width: 400, height: '4em' }}>
				<SkeletonItem />
			</Skeleton>
		</Stack.Item>
		<Stack.Item>
			<AssetCardSkeleton icon={<Database32Regular />} />
		</Stack.Item>
	</Stack>
);

type createArtifactConfig = {
	id: string;
	type: SupportedArtifactType;
	workspaceId: string;
	workspaceName: string;
	accessToken: string;
	selectedArtifactName?: string;
};
const DataSourceContent: FC = () => {
	const { extensionClient } = useFabricContext();
	const { user_access, bootoutput } = useLoginContext();
	const [loading, setLoading] = useState(false);
	const styles = useStyles();
	const { t } = useDQTranslation();
	const { workspaceObjectId } = useParams<{ workspaceObjectId: string }>();
	const { logger } = useIDQALoggerContext();
	const {
		bootoutput: { orgId },
	} = useLoginContext();
	const createFabricArtifactWithCS = async ({ id, workspaceId, workspaceName, accessToken, selectedArtifactName, type }: createArtifactConfig) => {
		let csDetails: Awaited<ReturnType<typeof catalogSourceBuilder.create>>;
		try {
			csDetails = await catalogSourceBuilder.create(type, id, workspaceId, workspaceName, accessToken, orgId);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (exception: any) {
			const message = exception.message || t(i18nKeys.main.genericCreateCSError);
			throw { title: t(i18nKeys.main.csCreateNotificationTitle), message };
		}
		try {
			const dqArtifact = await callArtifactCreate({
				workspaceObjectId: workspaceId,
				workloadPayload: {
					srcArtifact: {
						id,
						workspaceId,
						type,
					},
					catalogSrc: {
						id: csDetails.response.id as string,
						name: csDetails.payload.name as string,
						type: csDetails.payload.type as SupportedCatalogSourceType,
					},
				},
				itemType: getItemType(),
				displayName: selectedArtifactName || generateArtifactName(id, workspaceId),
				description: 'This is DQ artifact created for informatica fabric integration',
				extensionClient,
				externalCatalogSrcLinkFlow: !csDetails.new_source,
			});
			return dqArtifact;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (exception: any) {
			let message = exception.message;
			if ('error' in exception) {
				const details = parseFabricException(exception);
				message = details.message;
				//Cleanup CS id artifact creation failed.
				if (csDetails.new_source) {
					await catalogSourceBuilder.delete(type, id);
				}
			}
			throw { title: t(i18nKeys.main.csArtifactNotificationTitle), message };
		}
	};
	const checkForExistingArtifact = async ({
		type,
		workspaceId,
		srcArtifactId,
		warehouseName,
	}: {
		workspaceId: string;
		type: SupportedArtifactType;
		srcArtifactId: string;
		warehouseName: string;
	}): Promise<{ isNewArtifact?: boolean; catalogSrcExists: boolean; artifactDetails?: { artifactId: string; displayName: string } }> => {
		const catalogSource = await searchCatalogFromExternalURIs({ workspaceId, artifactType: type, artifactId: srcArtifactId });
		const existingCsName = catalogSource.hits[0]?.[CoreAttr.Name];
		if (existingCsName) {
			const externalURI = catalogSource.hits[0]?.[constants.EXTERNAL_URI];
			/**
			 * ? if catalog source is linked externally, we create the artifact using these details
			 */
			if (externalURI.length < 2 || !externalURI[1] || externalURI[1].length === 0) {
				logger.info('Externally Linked CS');
				try {
					const csDetails = catalogSource.hits[0];
					const response = await callArtifactCreate({
						workspaceObjectId: workspaceId,
						itemType: getItemType(),
						displayName: warehouseName,
						description: 'This is DQ artifact created for informatica fabric integration',
						workloadPayload: {
							catalogSrc: {
								id: csDetails[CoreAttr.Origin],
								name: csDetails[CoreAttr.Name],
								type: 'Fabric Data Warehouse' as SupportedCatalogSourceType,
							},
							srcArtifact: {
								id: srcArtifactId,
								workspaceId,
								type,
							},
						},
						extensionClient,
						externalCatalogSrcLinkFlow: true,
					});
					return { isNewArtifact: true, catalogSrcExists: true, artifactDetails: { displayName: response.displayName, artifactId: response.id } };
				} catch (err) {
					throw new Error('something went wrong while creating artifact');
				}
			}
			/**
			 * ! using externalURI to get DqArtifactId for navigation to IDQA details page for the artifact
			 */
			const { artifactId } = getFabricDetails(externalURI[1]);
			try {
				const artifactDetails = await extensionClient.itemCrud.getItem({ objectId: artifactId });
				extensionClient.notification.open({
					title: t(i18nKeys.main.artifactAlreadyExist, { artifactName: artifactDetails.displayName }),
					notificationType: NotificationType.Warning,
					duration: NotificationToastDuration.Long,
					buttons: [
						{
							action: constants.FABRIC_ACTIONS.ARTIFACT_ALREADY_EXISTS,
							workloadName: getWorkloadName(),
							data: { artifactId },
							label: t(i18nKeys.main.goToArtfact, { artifactName: artifactDetails.displayName }),
						},
					],
				});

				return { catalogSrcExists: true, artifactDetails: { artifactId, displayName: artifactDetails.displayName } };
			} catch (err) {
				logger.error('Getting Artifact details via MS Api failed.');
				throw new Error('Getting Artifact details failed.');
			}
		}
		return { catalogSrcExists: false };
	};
	const createDQArtifact = async ({ workspaceObjectId, itemObjectId, datahubItemUI }: SelectedArtifactDatahub) => {
		setLoading(true);
		try {
			if (!datahubItemUI) {
				throw new Error(t(i18nKeys.main.artifactCreationFailedError));
			}
			const createdArtifact: { artifactId?: string; displayName?: string } = {};
			let newArtifact = false;
			const type = datahubItemUI.fabricItemType as SupportedArtifactType;
			const { catalogSrcExists, artifactDetails, isNewArtifact } = await checkForExistingArtifact({
				type,
				workspaceId: workspaceObjectId,
				srcArtifactId: itemObjectId,
				warehouseName: datahubItemUI.displayName,
			});
			/**
			 * * no associated catalog source exists and i
			 * */
			if (!catalogSrcExists && datahubItemUI) {
				const response = await createFabricArtifactWithCS({
					workspaceId: workspaceObjectId,
					workspaceName: datahubItemUI.workspaceName,
					id: itemObjectId,
					selectedArtifactName: datahubItemUI.displayName,
					type,
					accessToken: bootoutput.fabricToken,
				});
				createdArtifact.artifactId = response.id;
				createdArtifact.displayName = response.displayName;
				newArtifact = true;
			} else {
				if (artifactDetails) {
					createdArtifact.artifactId = artifactDetails.artifactId;
					createdArtifact.displayName = artifactDetails.displayName;
					newArtifact = isNewArtifact ? true : false;
				}
			}

			if (createdArtifact) {
				if (newArtifact) {
					extensionClient.notification.open({
						title: t(i18nKeys.main.createArtifactSuccess, {
							artifactName: createdArtifact.displayName as string,
						}),
						notificationType: NotificationType.Success,
						duration: NotificationToastDuration.Short,
					});
				}

				extensionClient.page.open({
					workloadName: getWorkloadName(),
					route: {
						path: buildURL(ROUTES.dataQuality, {
							assetId: createdArtifact.artifactId as string,
						}),
					},
				});
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (exception: any) {
			extensionClient.notification.open({
				title: exception.title,
				message: exception.message,
				notificationType: NotificationType.Error,
				duration: NotificationToastDuration.Medium,
			});
		} finally {
			setLoading(false);
		}
	};

	const onCardClick = React.useCallback(async () => {
		const selection = await extensionClient.datahub.openDialog({
			filters: [{ datahubFilterType: DatahubFilterType.workspace, values: [workspaceObjectId] }],
			workspaceNavigationEnabled: false,
			supportedTypes: ['Warehouse'],
			dialogDescription: t(i18nKeys.main.pickSrcArtifact),
		});
		if (selection.selectedDatahubItem && selection.selectedDatahubItem.length > 0) {
			createDQArtifact(selection.selectedDatahubItem[0]);
		}
	}, [extensionClient, workspaceObjectId]);

	return (
		<Page>
			{user_access === 'READ_ONLY' ? (
				<ErrorScreen message={t(i18nKeys.main.cannotCreateAssetMsg)} />
			) : (
				<React.Suspense fallback={<Spinner style={{ height: '100%' }} />}>
					<Stack className={styles.panel} tokens={{ childrenGap: loading === false ? 10 : 5 }}>
						{loading === false ? (
							<>
								<Stack.Item>
									<Text size={500} weight="semibold">
										{t(i18nKeys.main.addDataToStartCreatingAQualityAsset)}
									</Text>
								</Stack.Item>
								<Stack.Item>
									<Stack className={styles.cardContainer} horizontal tokens={{ childrenGap: 20 }} wrap>
										<StackItem>
											<AssetCard
												id="withinLake"
												icon={<Database32Regular />}
												text={t(i18nKeys.main.selectSourceWithinOneLake)}
												backgroudColor="#f3f2f1"
												onCardClick={onCardClick}
											/>
										</StackItem>
									</Stack>
								</Stack.Item>
							</>
						) : (
							<>
								<Stack.Item>
									<DataUsageRegular className={styles.dataUsage} />
								</Stack.Item>
								<Stack.Item>
									<Text size={400} weight={'medium'}>
										{t(i18nKeys.main.creatingDQArtifact)}
									</Text>
								</Stack.Item>
								<Stack.Item>
									<ProgressBar className={styles.progressBar} />
								</Stack.Item>
							</>
						)}
					</Stack>
				</React.Suspense>
			)}
		</Page>
	);
};

const DataSource: FC = () => (
	<Page>
		<DataSourceContent />
	</Page>
);

export default DataSource;
